/**
 * Related Posts - 블로그 상세 하단 관련 글 데이터
 *
 * CMS 원본이 없는 파생 캐시다. 기준 글의 태그·저자를 신호로 후보 글에서 6개를 뽑아
 * slug 만 포스트별로 저장한다(`getRelatedPosts-{slug}`). 라이브 WordPress 조회는 하지 않는다.
 *
 * 표시 데이터(제목·이미지)는 담지 않는다 — 블로그 상세 페이지 로더가 각 글의 요약 캐시
 * (`getPostSummary-{slug}`)를 읽어 얻는다. 박제하면 그 글의 제목만 고쳐도 이 캐시가
 * 낡는데, 이 캐시는 기준 글이 저장될 때만 재생성되므로 스스로 낫지 않는다.
 *
 * 6개를 저장하고 3개를 쓰는 이유 — 소비 시점에 멤버가 삭제·비공개되면 탈락하는데
 * 이 캐시는 기준 글 저장 때만 갱신되므로 탈락이 누적된다. 예비 3개로 섹션이 비는 걸 막는다.
 *
 * ⚠️ 후보(candidates)는 "그 환경의 블로그 글 전체(유니버스)"여야 한다 — pool(태그·저자 매칭)과
 * 부족분 채우기(pool 밖 최신글)를 같은 배열에서 유도하기 때문이다. 태그·저자 캐시처럼 이미
 * 좁혀진 집합을 넘기면 채울 대상이 사라져 6개 미만이 조용히 저장된다. pool 을 좁힌 소스로
 * 바꾸려면 pool 과 유니버스를 별도 입력으로 쪼개야 한다.
 */

import { CONTENT_CATEGORY_ID } from '@/constants/content-categoryId';
import { toEpoch } from '@/utils/date';
import { fetchFromCache, FetchAPIOptions } from './api';
import {
  getBlogNewsPostsForList,
  type BlogNewsListItemProp,
} from './blog-news';
import { uploadWordpressCacheDataToS3 } from './wordpress-cache';

// ============================================
// Types
// ============================================

export interface RelatedPostsData {
  slug: string;
  /** 추첨 회차 — 수동 재추첨 시에만 올라가고 포스트 저장 시 0으로 리셋된다 */
  seed: number;
  /** 관련 글 slug 6개 — 표시 데이터는 요약 캐시(getPostSummary-{slug})에서 읽는다 */
  slugs: string[];
}

interface BuildOptions {
  /** 후보 글 유니버스 (위 모듈 주석 참고). 생략하면 블로그 목록 캐시를 조회한다 */
  candidates?: BlogNewsListItemProp[];
  seed?: number;
}

// ============================================
// 상수
// ============================================

/**
 * 캐시 폴더명이자 revalidateTag 태그 — 시리즈·태그의 per-slug 라벨 관례를 따른다.
 * 조회 함수에 `Data` 가 붙은 것은 기존 유틸 `getRelatedPosts`(`@/utils/blog-news`)와
 * 이름이 겹쳐서다.
 */
export const getRelatedPostsCacheLabel = (slug: string) =>
  `getRelatedPosts-${slug}`;

/** 저장 개수. 블로그 상세 페이지 로더가 앞 3개만 쓰고 탈락 시 나머지를 예비로 쓴다 */
const RELATED_POSTS_STORED = 6;

const REROLL_MAX_ATTEMPTS = 20;

// ============================================
// 결정적 셔플
// ============================================

/** 문자열 → 32비트 시드 (FNV-1a) */
const toSeedNumber = (input: string): number => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
};

/** mulberry32 — 시드 하나로 재현 가능한 0~1 난수열 */
const createRandom = (seed: number) => {
  let state = seed >>> 0;

  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/** 시드 기반 Fisher-Yates — 같은 시드·같은 입력이면 항상 같은 순서 */
const shuffleWithSeed = (
  items: BlogNewsListItemProp[],
  seedInput: string,
): BlogNewsListItemProp[] => {
  const random = createRandom(toSeedNumber(seedInput));
  const shuffled = [...items];

  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
};

// ============================================
// 추출 (순수)
// ============================================

/** 포스트는 언어를 하나만 고를 수 있어 첫 항목만 비교한다 */
const isSameLocale = (
  post: BlogNewsListItemProp,
  base: BlogNewsListItemProp,
): boolean => post.lang?.[0] === base.lang?.[0];

/** 저자 글 개수 상한은 두지 않는다 — 같은 저자의 글은 그 자체로 연관성이 있다 */
export const pickRelatedPosts = (
  basePost: BlogNewsListItemProp,
  candidates: BlogNewsListItemProp[],
  seed = 0,
): string[] => {
  const baseTags = basePost.tags ?? [];
  const sameLocale = candidates.filter(
    post => post.slug !== basePost.slug && isSameLocale(post, basePost),
  );

  const pool = sameLocale.filter(
    post =>
      (!!basePost.authorId && post.authorId === basePost.authorId) ||
      (post.tags ?? []).some(tag => baseTags.includes(tag)),
  );

  const picked = shuffleWithSeed(pool, `${basePost.slug}:${seed}`).slice(
    0,
    RELATED_POSTS_STORED,
  );

  if (picked.length < RELATED_POSTS_STORED) {
    const pickedSlugs = new Set(picked.map(post => post.slug));
    const filler = sameLocale
      .filter(post => !pickedSlugs.has(post.slug))
      .sort((a, b) => toEpoch(b.date) - toEpoch(a.date))
      .slice(0, RELATED_POSTS_STORED - picked.length);

    picked.push(...filler);
  }

  return picked.map(post => post.slug);
};

/**
 * 직전 조합과 다른 6개가 나오는 회차를 찾는다. pool 이 작아 어떤 회차에서도 같은 집합만
 * 나오면 null — 순서만 바뀐 결과는 재추첨으로 보지 않는다.
 */
export const findRerollSeed = (
  basePost: BlogNewsListItemProp,
  candidates: BlogNewsListItemProp[],
  currentSeed: number,
  currentSlugs: string[],
): number | null => {
  const current = new Set(currentSlugs);

  for (let attempt = 1; attempt <= REROLL_MAX_ATTEMPTS; attempt += 1) {
    const seed = currentSeed + attempt;
    const slugs = pickRelatedPosts(basePost, candidates, seed);
    // 크기까지 비교하는 이유: 직전 캐시는 지금과 다른 후보 집합으로 만들어졌을 수 있어
    // (글 추가·삭제) 개수가 줄면 지금 결과가 직전 집합에 모두 포함될 수 있다.
    const changed =
      slugs.length !== current.size || slugs.some(slug => !current.has(slug));

    if (changed) return seed;
  }

  return null;
};

// ============================================
// 캐시 데이터 빌드
// ============================================

const findBasePost = (
  slug: string,
  candidates: BlogNewsListItemProp[],
): BlogNewsListItemProp => {
  const basePost = candidates.find(post => post.slug === slug);
  if (!basePost) {
    throw new Error(`블로그 포스트를 찾을 수 없습니다: ${slug}`);
  }

  return basePost;
};

const resolveCandidates = (candidates?: BlogNewsListItemProp[]) =>
  candidates ?? getBlogNewsPostsForList(CONTENT_CATEGORY_ID.blog);

// ============================================
// 조회 (빌드/런타임 + refresh)
// ============================================

/**
 * `fetchFromCache` 가 any 를 반환하므로 소비처의 `.slugs` 역참조 전에 형태를 확인한다.
 * 구 스키마(`posts: PostSummary[]`) 캐시는 여기서 걸러져 미스가 된다 — 배포보다 init 을
 * 먼저 돌려야 하는 이유다(설계 문서의 배포 절차 참고).
 */
const isRelatedPostsData = (value: unknown): value is RelatedPostsData => {
  if (!value || typeof value !== 'object') return false;

  const data = value as Partial<RelatedPostsData>;
  return (
    typeof data.slug === 'string' &&
    typeof data.seed === 'number' &&
    Array.isArray(data.slugs) &&
    data.slugs.every(slug => typeof slug === 'string')
  );
};

/**
 * 관련 글 데이터 조회 (refresh 시 실패하면 throw, 캐시 조회 실패는 null).
 *
 * `seed` 를 넘기지 않으면 0 으로 처음부터 다시 뽑는다 — 포스트 저장 훅이 쓰는 기본 경로다.
 * 캐시된 데이터만 소비하므로 `prodFilter` 는 관여하지 않는다 — 후보(또는 블로그 목록 캐시)가
 * 이미 환경별로 분리되어 있다.
 */
export const getRelatedPostsData = async (
  slug: string,
  options?: Pick<FetchAPIOptions, 'refresh'> & BuildOptions,
): Promise<RelatedPostsData | null> => {
  const label = getRelatedPostsCacheLabel(slug);

  if (options?.refresh) {
    const candidates = await resolveCandidates(options.candidates);
    const seed = options.seed ?? 0;
    const data: RelatedPostsData = {
      slug,
      seed,
      slugs: pickRelatedPosts(findBasePost(slug, candidates), candidates, seed),
    };

    await uploadWordpressCacheDataToS3(data, label);
    return data;
  }

  const cached = await fetchFromCache(label);
  return isRelatedPostsData(cached) ? cached : null;
};

/**
 * 재추첨 — 운영자가 명시적으로 요청할 때만 호출한다(포스트 저장 경로는 쓰지 않는다).
 * 다른 조합이 불가능하면 저장하지 않고 null 을 반환한다.
 */
export const rerollRelatedPostsCache = async (
  slug: string,
  candidates?: BlogNewsListItemProp[],
): Promise<RelatedPostsData | null> => {
  const [current, resolved] = await Promise.all([
    getRelatedPostsData(slug),
    resolveCandidates(candidates),
  ]);

  if (!current) {
    return getRelatedPostsData(slug, { refresh: true, candidates: resolved });
  }

  const seed = findRerollSeed(
    findBasePost(slug, resolved),
    resolved,
    current.seed,
    current.slugs,
  );

  if (seed === null) return null;

  return getRelatedPostsData(slug, {
    refresh: true,
    candidates: resolved,
    seed,
  });
};
